/**
 * Tarot card data — 22 Major Arcana + 56 Minor Arcana
 * All meanings in Spanish.
 */

export interface TarotCard {
  id: number;          // 0-77
  number: number;      // 0-21 for Major, 1-14 per suit for Minor
  arcana: "major" | "minor";
  suit?: "wands" | "cups" | "swords" | "pentacles";
  name: string;        // Spanish name
  nameEn: string;
  emoji: string;
  keywords: string[];
  upright: string;
  reversed: string;
  advice: string;
  numerology?: number; // numerological connection
}

// ─── MAJOR ARCANA ─────────────────────────────────────────────────────────────

export const MAJOR_ARCANA: TarotCard[] = [
  {
    id: 0, number: 0, arcana: "major", name: "El Loco", nameEn: "The Fool", emoji: "🃏",
    keywords: ["Comienzo", "Aventura", "Confianza", "Inocencia", "Libertad"],
    upright: "Un nuevo ciclo lleno de posibilidades. Un salto a lo desconocido con confianza en la vida. Es momento de actuar sin mirar atrás hacia tus miedos.",
    reversed: "Imprudencia, ignorar los riesgos. Quizá te frenas por miedo a empezar, o al contrario, actúas de forma demasiado impulsiva.",
    advice: "Confía en el proceso. El comienzo siempre parece impredecible: es normal.",
    numerology: 22,
  },
  {
    id: 1, number: 1, arcana: "major", name: "El Mago", nameEn: "The Magician", emoji: "🔮",
    keywords: ["Voluntad", "Maestría", "Creación", "Enfoque", "Recursos"],
    upright: "Tienes todas las herramientas: úsalas. La fuerza de la intención convierte las ideas en realidad. Es momento de concentración y de acción.",
    reversed: "Manipulación o no aprovechar tu potencial. Tienes los recursos, pero algo bloquea su uso.",
    advice: "Define qué es exactamente lo que quieres crear y dirige toda tu energía hacia ello.",
    numerology: 1,
  },
  {
    id: 2, number: 2, arcana: "major", name: "La Sacerdotisa", nameEn: "The High Priestess", emoji: "🌙",
    keywords: ["Intuición", "Misterio", "Subconsciente", "Sabiduría", "Paciencia"],
    upright: "Escucha tu voz interior. No todo necesita explicarse con la lógica. Es momento de acumular conocimiento y de observar en silencio.",
    reversed: "Ignorar la intuición, secretos, motivos ocultos. Puedes estar recibiendo información de las personas equivocadas.",
    advice: "Calla la mente y escucha lo que tu cuerpo ya sabe.",
    numerology: 2,
  },
  {
    id: 3, number: 3, arcana: "major", name: "La Emperatriz", nameEn: "The Empress", emoji: "👑",
    keywords: ["Fertilidad", "Abundancia", "Creatividad", "Naturaleza", "Maternidad"],
    upright: "Momento de crecimiento, creatividad y abundancia. Todo lo que cultivas con amor dará frutos. Conexión con la naturaleza y la sensualidad.",
    reversed: "Dependencia, sobreprotección, bloqueo creativo. Quizá estás agotada por cuidar de los demás y te olvidas de ti misma.",
    advice: "Crea. Cuídate con la misma ternura con que cuidas a quienes te rodean.",
    numerology: 3,
  },
  {
    id: 4, number: 4, arcana: "major", name: "El Emperador", nameEn: "The Emperor", emoji: "⚔️",
    keywords: ["Poder", "Estructura", "Orden", "Paternidad", "Estabilidad"],
    upright: "Asume la responsabilidad y establece reglas claras. Una base estable crea espacio para crecer.",
    reversed: "Tiranía, rigidez, debilidad disfrazada de poder. O bien miedo a asumir la responsabilidad.",
    advice: "Construye un sistema. La estructura no es una limitación, sino el cimiento de la libertad.",
    numerology: 4,
  },
  {
    id: 5, number: 5, arcana: "major", name: "El Hierofante", nameEn: "The Hierophant", emoji: "⛪",
    keywords: ["Tradición", "Mentoría", "Ritual", "Fe", "Instituciones"],
    upright: "Recurre a la tradición o a un mentor. Hay valor en los caminos probados. Es momento de aprender o de transmitir conocimiento.",
    reversed: "Seguir dogmas a ciegas. Es hora de cuestionar las reglas ajenas y encontrar tu propio sistema de valores.",
    advice: "Encuentra el equilibrio entre el respeto a las tradiciones y tu independencia personal.",
    numerology: 5,
  },
  {
    id: 6, number: 6, arcana: "major", name: "Los Enamorados", nameEn: "The Lovers", emoji: "💕",
    keywords: ["Elección", "Amor", "Valores", "Unión", "Armonía"],
    upright: "Tienes ante ti una elección importante: refleja tus valores más profundos. En las relaciones, unión, armonía y reciprocidad.",
    reversed: "Una mala elección, desequilibrio en las relaciones, traición a tus propios valores.",
    advice: "Toma la decisión que corresponda a quien quieres llegar a ser.",
    numerology: 6,
  },
  {
    id: 7, number: 7, arcana: "major", name: "El Carro", nameEn: "The Chariot", emoji: "🏆",
    keywords: ["Victoria", "Voluntad", "Control", "Movimiento", "Determinación"],
    upright: "Victoria a través de la disciplina y la voluntad. Mantén bajo control las fuerzas contradictorias y avanza.",
    reversed: "Pérdida de rumbo, agresividad sin propósito, impulsos incontrolables.",
    advice: "Define hacia dónde te diriges. La victoria es posible, pero necesitas una meta clara.",
    numerology: 7,
  },
  {
    id: 8, number: 8, arcana: "major", name: "La Fuerza", nameEn: "Strength", emoji: "🦁",
    keywords: ["Coraje", "Paciencia", "Ternura", "Pasión", "Fuerza interior"],
    upright: "La verdadera fuerza está en la suavidad, no en la represión. Doma tus miedos e instintos con amor, no con la lucha.",
    reversed: "Miedo, dudas sobre ti misma, o al contrario, dureza donde se necesita flexibilidad.",
    advice: "Tu vulnerabilidad también es fuerza. No temas ser suave.",
    numerology: 8,
  },
  {
    id: 9, number: 9, arcana: "major", name: "El Ermitaño", nameEn: "The Hermit", emoji: "🏔️",
    keywords: ["Soledad", "Búsqueda", "Sabiduría", "Luz interior", "Pausa"],
    upright: "Momento de soledad y búsqueda interior. La sabiduría llega en el silencio. Tu luz mostrará el camino a los demás.",
    reversed: "Aislamiento por miedo, rechazo a relacionarte con el mundo o a compartir tu luz.",
    advice: "Permítete estar en silencio, no como huida, sino como fuente.",
    numerology: 9,
  },
  {
    id: 10, number: 10, arcana: "major", name: "La Rueda de la Fortuna", nameEn: "Wheel of Fortune", emoji: "☸️",
    keywords: ["Ciclicidad", "Suerte", "Cambios", "Karma", "Destino"],
    upright: "La rueda gira, y hacia lo mejor. Acepta los cambios como una parte natural de la vida. Tu karma vuelve a ti.",
    reversed: "Resistencia al cambio, mala suerte, la sensación de estar a merced de fuerzas externas.",
    advice: "Lo que sembraste, ahora lo cosechas. Lo que siembres hoy definirá el próximo giro.",
    numerology: 10,
  },
  {
    id: 11, number: 11, arcana: "major", name: "La Justicia", nameEn: "Justice", emoji: "⚖️",
    keywords: ["Equilibrio", "Verdad", "Karma", "Responsabilidad", "Honestidad"],
    upright: "Momento de mirar la situación con honestidad. Las decisiones que tomes ahora tienen consecuencias. La justicia prevalecerá.",
    reversed: "Injusticia, negar la responsabilidad, evitar las consecuencias de tus actos.",
    advice: "Sé honesta, ante todo contigo misma. El karma funciona con precisión.",
    numerology: 11,
  },
  {
    id: 12, number: 12, arcana: "major", name: "El Colgado", nameEn: "The Hanged Man", emoji: "🙃",
    keywords: ["Sacrificio", "Pausa", "Nueva mirada", "Soltar", "Espera"],
    upright: "Detente de forma voluntaria. La pausa revela lo que no se ve en el movimiento. Suelta el control y obtén una nueva perspectiva.",
    reversed: "Sacrificios innecesarios, demoras, resistencia a soltar lo que ya no funciona.",
    advice: "A veces, no hacer nada es una acción. Deja que la situación madure.",
    numerology: 12,
  },
  {
    id: 13, number: 13, arcana: "major", name: "La Muerte", nameEn: "Death", emoji: "🌑",
    keywords: ["Transformación", "Final", "Tránsito", "Renovación", "Soltar"],
    upright: "El final de un ciclo abre el siguiente. No es muerte, sino una transformación profunda. Suelta lo viejo: deja espacio para lo nuevo.",
    reversed: "Resistencia a cambios inevitables, quedarse atrapada en el pasado, miedo a la transformación.",
    advice: "Lo que termina debe terminar. Confía en el proceso de renovación.",
    numerology: 13,
  },
  {
    id: 14, number: 14, arcana: "major", name: "La Templanza", nameEn: "Temperance", emoji: "🌊",
    keywords: ["Equilibrio", "Paciencia", "Alquimia", "Templanza", "Fluir"],
    upright: "Mezcla los opuestos hasta lograr armonía. Los extremos agotan: encuentra tu ritmo. El movimiento lento es más firme que el rápido.",
    reversed: "Desequilibrio, extremos, impaciencia. Estás forzando algo que requiere tiempo.",
    advice: "Encuentra tu propio ritmo. Ni demasiado rápido, ni demasiado lento.",
    numerology: 14,
  },
  {
    id: 15, number: 15, arcana: "major", name: "El Diablo", nameEn: "The Devil", emoji: "🔗",
    keywords: ["Apego", "Miedo", "Ilusión", "Materialismo", "Liberación"],
    upright: "Te mantienes prisionera de tus propios miedos o adicciones. Las cadenas pueden quitarse: no son tan fuertes como parecen.",
    reversed: "Liberación de las ataduras, conciencia de la manipulación, ruptura de patrones dañinos.",
    advice: "Mira qué es lo que te retiene. A menudo, el carcelero eres tú misma.",
    numerology: 15,
  },
  {
    id: 16, number: 16, arcana: "major", name: "La Torre", nameEn: "The Tower", emoji: "⚡",
    keywords: ["Destrucción", "Revelación", "Caos", "Liberación", "Despertar"],
    upright: "Destrucción repentina de lo que se construyó sobre cimientos falsos. Doloroso, pero necesario. Tras el caos, la liberación.",
    reversed: "Evitar el derrumbe inevitable, miedo al cambio, o una crisis que ya pasó.",
    advice: "Cuando algo se derrumba, es que ya tocaba. Reconstruye sobre una base honesta.",
    numerology: 16,
  },
  {
    id: 17, number: 17, arcana: "major", name: "La Estrella", nameEn: "The Star", emoji: "⭐",
    keywords: ["Esperanza", "Sanación", "Inspiración", "Vulnerabilidad", "Renovación"],
    upright: "Tras la tormenta, la luz. Momento de esperanza, sanación y renovación. Confía en el futuro: es mejor de lo que ahora parece.",
    reversed: "Desesperación, pérdida de la fe, la sensación de que la suerte te ha abandonado.",
    advice: "Sé vulnerable. La apertura y la esperanza no son debilidad, sino fuente de fuerza.",
    numerology: 17,
  },
  {
    id: 18, number: 18, arcana: "major", name: "La Luna", nameEn: "The Moon", emoji: "🌕",
    keywords: ["Ilusión", "Subconsciente", "Miedo", "Intuición", "Lo oculto"],
    upright: "Las cosas no son lo que parecen. Los miedos del subconsciente afloran a la superficie. Presta atención a tus sueños y a los símbolos.",
    reversed: "Liberación de las ilusiones, claridad tras la niebla, salida de un periodo de incertidumbre.",
    advice: "No tomes decisiones importantes en la niebla. Deja que la claridad llegue.",
    numerology: 18,
  },
  {
    id: 19, number: 19, arcana: "major", name: "El Sol", nameEn: "The Sun", emoji: "☀️",
    keywords: ["Alegría", "Éxito", "Vitalidad", "Claridad", "Apertura infantil"],
    upright: "Momento de alegría, éxito y vitalidad. Todo está claro, todo es posible. La ligereza y la apertura no son un pecado, sino sabiduría.",
    reversed: "Bajón temporal de energía, autodesprecio, optimismo poco realista.",
    advice: "Permítete disfrutar. La ligereza también es profundidad.",
    numerology: 19,
  },
  {
    id: 20, number: 20, arcana: "major", name: "El Juicio", nameEn: "Judgement", emoji: "🎺",
    keywords: ["Despertar", "Llamado", "Perdón", "Transformación", "Reevaluación"],
    upright: "Escuchas la llamada de tu propósito. Momento de reevaluar el pasado con honestidad y de perdonar, a ti misma y a los demás.",
    reversed: "Ignorar la llamada interior, autoflagelación, miedo a ser juzgada.",
    advice: "Perdónate. No es justificar el pasado, es liberarte para el futuro.",
    numerology: 20,
  },
  {
    id: 21, number: 21, arcana: "major", name: "El Mundo", nameEn: "The World", emoji: "🌍",
    keywords: ["Final", "Integración", "Éxito", "Plenitud", "Nuevo ciclo"],
    upright: "El ciclo se ha completado, con una sensación de plenitud y logro. Integración de todo lo vivido. Lista para una nueva vuelta.",
    reversed: "Algo inconcluso, evitar el paso final, incapacidad de aceptar el éxito.",
    advice: "Ya has llegado. Reconócelo y ábrete al siguiente viaje.",
    numerology: 21,
  },
];

// ─── MINOR ARCANA ─────────────────────────────────────────────────────────────

const SUIT_ELEMENT: Record<string, { element: string; theme: string; emoji: string }> = {
  wands:     { element: "Fuego",  theme: "Voluntad, creatividad, carrera, pasión",          emoji: "🔥" },
  cups:      { element: "Agua",   theme: "Sentimientos, intuición, relaciones, alma",       emoji: "💧" },
  swords:    { element: "Aire",   theme: "Pensamientos, conflictos, comunicación, decisiones", emoji: "🌬️" },
  pentacles: { element: "Tierra", theme: "Dinero, cuerpo, materialidad, estabilidad",       emoji: "🌿" },
};

const SUIT_CARDS: Record<string, Record<number, { name: string; upright: string; reversed: string }>> = {
  wands: {
    1:  { name: "As de Bastos",    upright: "Un destello de inspiración, un nuevo proyecto creativo, el despertar de la pasión.",                       reversed: "Demora en el comienzo, bloqueo creativo, potencial desperdiciado." },
    2:  { name: "Dos de Bastos",      upright: "Planificar la expansión, una mirada audaz al futuro, la espera del resultado.",                            reversed: "Indecisión, miedo a arriesgar, horizonte limitado." },
    3:  { name: "Tres de Bastos",      upright: "Los primeros frutos, ampliación de horizontes, alianza para avanzar.",                                     reversed: "Aplazamiento, lentitud, oportunidades perdidas." },
    4:  { name: "Cuatro de Bastos",      upright: "Celebración, estabilidad, armonía en la familia o el equipo, una base lograda.",                          reversed: "Inestabilidad en el hogar, armonía incompleta, periodo de transición." },
    5:  { name: "Cinco de Bastos",      upright: "Rivalidad, choque de ideas, una energía caótica que pide ser encauzada.",                                reversed: "Evitar el conflicto o resolverlo, la lucha interior se calma." },
    6:  { name: "Seis de Bastos",      upright: "Victoria, reconocimiento, éxito público, regreso triunfal.",                                              reversed: "Soberbia, victoria oculta sin reconocimiento, éxito demorado." },
    7:  { name: "Siete de Bastos",      upright: "Defensa de tu posición, firmeza frente a la oposición, lucha por tus valores.",                          reversed: "Ceder terreno, atacar cuando hace falta defender, agotamiento." },
    8:  { name: "Ocho de Bastos",      upright: "Movimiento rápido, noticias en camino, vertiginoso avance de los acontecimientos.",                      reversed: "Demoras, lentitud, dispersión de rumbos." },
    9:  { name: "Nueve de Bastos",      upright: "Firmeza a pesar del cansancio, defensa de lo acumulado, la última línea.",                               reversed: "Paranoia, exceso de actitud defensiva, rechazo a la ayuda." },
    10: { name: "Diez de Bastos",     upright: "Una carga pesada, sobrecarga de responsabilidad: es hora de delegar.",                                   reversed: "Soltar el peso, liberación de obligaciones, redistribución." },
    11: { name: "Sota de Bastos",    upright: "El entusiasmo del principiante, ideas audaces, una mirada ardiente al mundo.",                          reversed: "Inconstancia, energía caótica, comienzos sin terminar." },
    12: { name: "Caballero de Bastos", upright: "Acción vertiginosa, aventura, una audacia que a veces roza la imprudencia.",                         reversed: "Impulsividad, agresividad, lo empezado y abandonado." },
    13: { name: "Reina de Bastos", upright: "Carisma, seguridad, independencia, energía de liderazgo creativo.",                                       reversed: "Celos, manipulación, narcisismo." },
    14: { name: "Rey de Bastos",   upright: "Liderazgo visionario, audacia emprendedora, poder carismático.",                                           reversed: "Tiranía, arrogancia, potencial de líder no realizado." },
  },
  cups: {
    1:  { name: "As de Copas",    upright: "Un nuevo sentimiento, el amor se abre, un flujo intuitivo, plenitud espiritual.",                          reversed: "Sentimientos bloqueados, vacío emocional." },
    2:  { name: "Dos de Copas",      upright: "Unión, atracción mutua, una alianza entre iguales.",                                                       reversed: "Desarmonía, ruptura de vínculos, desequilibrio en la relación." },
    3:  { name: "Tres de Copas",      upright: "Amistad, celebración, creación compartida, la alegría del encuentro.",                                     reversed: "Chismes, exceso, aislamiento de los amigos." },
    4:  { name: "Cuatro de Copas",      upright: "Apatía, aburrimiento, oportunidades perdidas por encerrarte en ti misma.",                                reversed: "Salir de la apatía, aceptar una nueva propuesta." },
    5:  { name: "Cinco de Copas",      upright: "Pena, decepción, lamento por las pérdidas, pero no todo está perdido.",                                    reversed: "Aceptar las pérdidas, seguir adelante, sanación." },
    6:  { name: "Seis de Copas",      upright: "Nostalgia, recuerdos de la infancia, inocencia, regalos del pasado.",                                      reversed: "Quedarse atrapada en el pasado, resistencia a madurar." },
    7:  { name: "Siete de Copas",      upright: "Ilusiones, muchas opciones, fantasías que necesitan un contraste con la realidad.",                       reversed: "Claridad tras la niebla, decisión tomada, foco recuperado." },
    8:  { name: "Ocho de Copas",      upright: "Alejarse de lo que ya no tiene sentido. Búsqueda de profundidad.",                                        reversed: "Miedo al cambio, resistencia a dejar el pasado." },
    9:  { name: "Nueve de Copas",      upright: "Cumplimiento de los deseos, satisfacción, bienestar emocional.",                                          reversed: "Materialismo, deseos superficiales, insatisfacción." },
    10: { name: "Diez de Copas",     upright: "Felicidad, armonía familiar, plenitud emocional, el arcoíris tras la lluvia.",                            reversed: "Ruptura de la armonía, conflictos familiares, ideales inalcanzables." },
    11: { name: "Sota de Copas",    upright: "Inspiración creativa, apertura emocional, mensajes intuitivos.",                                          reversed: "Inmadurez de los sentimientos, refugio en la fantasía, manipulación emocional." },
    12: { name: "Caballero de Copas", upright: "Búsqueda romántica, perseguir un sueño, sensibilidad en acción.",                                      reversed: "Soñar sin actuar, celos, inestabilidad." },
    13: { name: "Reina de Copas", upright: "Empatía, intuición, madurez emocional, compasión.",                                                        reversed: "Dependencia emocional, sobrecarga con los sentimientos ajenos." },
    14: { name: "Rey de Copas",   upright: "Equilibrio emocional, sabiduría en las relaciones, diplomacia del corazón.",                              reversed: "Reprimir los sentimientos, manipulación a través de las emociones." },
  },
  swords: {
    1:  { name: "As de Espadas",     upright: "Claridad mental, verdad, un avance que rompe las ilusiones, el inicio de una nueva comprensión.",       reversed: "Mentira, confusión, crueldad sin sabiduría." },
    2:  { name: "Dos de Espadas",       upright: "Una elección congelada, equilibrio a través del cierre, un punto muerto que pide solución.",            reversed: "Alivio de la tensión, decisión tomada, aunque puede ser dolorosa." },
    3:  { name: "Tres de Espadas",       upright: "Dolor, ruptura, pena: reconocerlo con honestidad ayuda a sanar.",                                       reversed: "Liberación del dolor, perdón, una sanación largamente esperada." },
    4:  { name: "Cuatro de Espadas",       upright: "Descanso, recuperación tras el conflicto, una pausa necesaria.",                                        reversed: "Parada forzada o salir del descanso antes de tiempo." },
    5:  { name: "Cinco de Espadas",       upright: "Victoria a cualquier precio, conflicto, una victoria pírrica: ¿vale la pena?",                          reversed: "Reconciliación, alejarse del conflicto, consecuencias dolorosas." },
    6:  { name: "Seis de Espadas",       upright: "Tránsito hacia algo mejor, alejarse de la tormenta, recuperación gradual.",                            reversed: "Demora en los cambios, resistencia a dejar el pasado." },
    7:  { name: "Siete de Espadas",       upright: "Astucia, estrategia, actuar por vías indirectas, a veces engaño.",                                      reversed: "Descubrimiento, reconocer la verdad, decisiones impulsivas." },
    8:  { name: "Ocho de Espadas",       upright: "Autolimitación, una trampa hecha de pensamientos, una sensación de impotencia que es ilusión.",         reversed: "Liberación al cambiar la forma de pensar: hay salida." },
    9:  { name: "Nueve de Espadas",       upright: "Ansiedad, miedos nocturnos, catastrofismo: el sufrimiento suele estar en la cabeza.",                  reversed: "Salir de la depresión, aceptar ayuda, el fin de la pesadilla." },
    10: { name: "Diez de Espadas",      upright: "Un final, un cierre doloroso, el fondo, pero después solo queda subir.",                                reversed: "Lo inevitable aceptado, comienza la recuperación lenta." },
    11: { name: "Sota de Espadas",     upright: "Mente aguda, curiosidad, franqueza que a veces roza la brusquedad.",                                  reversed: "Chismes, superficialidad, dispersión mental." },
    12: { name: "Caballero de Espadas", upright: "Pensamiento veloz, determinación, ambición, a veces demasiado vertiginosa.",                        reversed: "Impulsividad, conflictividad, palabras que hieren como una espada." },
    13: { name: "Reina de Espadas", upright: "Mente aguda, independencia, honestidad a veces dolorosa, sabiduría ganada con la experiencia.",          reversed: "Amargura, crueldad, cerrarse a quienes quieren ayudar." },
    14: { name: "Rey de Espadas",   upright: "Autoridad a través del intelecto, justicia, decisiones claras, liderazgo ético.",                       reversed: "Tiranía de la mente, manipulación, crueldad sin compasión." },
  },
  pentacles: {
    1:  { name: "As de Oros",   upright: "Un arranque material, dinero nuevo, una oportunidad para crecer.",                                            reversed: "Oportunidad perdida, inestabilidad financiera al principio." },
    2:  { name: "Dos de Oros",     upright: "Hacer malabares con los recursos, adaptabilidad en las finanzas, equilibrio de tareas.",                     reversed: "Desequilibrio financiero, exceso de obligaciones." },
    3:  { name: "Tres de Oros",     upright: "Maestría a través de la práctica, aporte en equipo, reconocimiento de la calidad del trabajo.",             reversed: "Subestimar el trabajo, falta de trabajo en equipo, mediocridad." },
    4:  { name: "Cuatro de Oros",     upright: "Preservar los recursos, seguridad financiera, pero sin avaricia.",                                           reversed: "Avaricia, materialismo, miedo a perder el control." },
    5:  { name: "Cinco de Oros",     upright: "Dificultades financieras, necesidad: la ayuda está cerca, vale la pena pedirla.",                            reversed: "Recuperación de las finanzas, aceptar ayuda, salir de la crisis." },
    6:  { name: "Seis de Oros",     upright: "Generosidad, equilibrio entre dar y recibir, distribución justa.",                                          reversed: "Avaricia, deudas, desequilibrio en el dar y el recibir." },
    7:  { name: "Siete de Oros",     upright: "Una pausa para evaluar el crecimiento, paciencia en los proyectos a largo plazo.",                          reversed: "Impaciencia, falta de retorno, inversiones equivocadas." },
    8:  { name: "Ocho de Oros",     upright: "Trabajo aplicado, desarrollo de la maestría, aprender haciendo.",                                            reversed: "Perfeccionismo que frena la acción, monotonía sin propósito." },
    9:  { name: "Nueve de Oros",     upright: "Independencia financiera, lujo ganado con esfuerzo, autosuficiencia.",                                       reversed: "Dependencia material, sobrevalorar lo material." },
    10: { name: "Diez de Oros",    upright: "Bienestar a largo plazo, legado familiar, un cimiento sólido.",                                              reversed: "Disputas financieras en la familia, un legado inestable." },
    11: { name: "Sota de Oros",   upright: "Aplicación, aprendizaje práctico, los primeros pasos hacia una meta material.",                            reversed: "Procrastinación, falta de practicidad, resistencia a aprender." },
    12: { name: "Caballero de Oros", upright: "Método, fiabilidad, un progreso lento pero seguro.",                                                    reversed: "Pereza, monotonía, aferrarse con terquedad a un método que no funciona." },
    13: { name: "Reina de Oros", upright: "Cuidado práctico, sabiduría financiera, hogar y nutrición a través de la acción.",                          reversed: "Sobreprotección, dependencia financiera, descuido de una misma." },
    14: { name: "Rey de Oros",   upright: "Éxito financiero, fiabilidad, liderazgo práctico, abundancia.",                                              reversed: "Codicia, corrupción, materialismo terco." },
  },
};

// ─── Minor Arcana English names ───────────────────────────────────────────────

const RANK_EN: Record<number, string> = {
  1: "Ace", 2: "Two", 3: "Three", 4: "Four", 5: "Five",
  6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten",
  11: "Page", 12: "Knight", 13: "Queen", 14: "King",
};
const SUIT_EN: Record<string, string> = {
  wands: "Wands", cups: "Cups", swords: "Swords", pentacles: "Pentacles",
};

// Per-card keywords for Minor Arcana
const MINOR_KEYWORDS: Record<string, Record<number, string[]>> = {
  wands: {
    1:  ["Inspiración", "Comienzo", "Pasión", "Potencial"],
    2:  ["Planificación", "Expansión", "Audacia", "Elección"],
    3:  ["Primeros frutos", "Horizonte", "Alianza", "Espera"],
    4:  ["Celebración", "Estabilidad", "Armonía", "Éxito"],
    5:  ["Rivalidad", "Conflicto", "Energía", "Desafío"],
    6:  ["Victoria", "Reconocimiento", "Liderazgo", "Triunfo"],
    7:  ["Defensa", "Firmeza", "Posición", "Lucha"],
    8:  ["Velocidad", "Movimiento", "Noticias", "Impulso"],
    9:  ["Firmeza", "Cansancio", "Frontera", "Resistencia"],
    10: ["Sobrecarga", "Responsabilidad", "Delegación", "Carga"],
    11: ["Entusiasmo", "Ideas", "Inicio del camino", "Apertura"],
    12: ["Acción", "Aventura", "Pasión", "Velocidad"],
    13: ["Carisma", "Seguridad", "Independencia", "Liderazgo"],
    14: ["Visión", "Emprendimiento", "Poder", "Estrategia"],
  },
  cups: {
    1:  ["Amor", "Sentimientos", "Intuición", "Apertura"],
    2:  ["Unión", "Reciprocidad", "Alianza", "Atracción"],
    3:  ["Amistad", "Celebración", "Alegría", "Creatividad"],
    4:  ["Apatía", "Aburrimiento", "Reflexión", "Lo perdido"],
    5:  ["Pena", "Pérdida", "Lamento", "Sanación"],
    6:  ["Nostalgia", "Infancia", "Inocencia", "Pasado"],
    7:  ["Ilusiones", "Fantasías", "Elección", "Sueños"],
    8:  ["Partida", "Búsqueda de sentido", "Cambios", "Profundidad"],
    9:  ["Deseo", "Satisfacción", "Éxito", "Bienestar"],
    10: ["Felicidad", "Familia", "Armonía", "Plenitud"],
    11: ["Inspiración", "Intuición", "Apertura", "Creatividad"],
    12: ["Romance", "Sueño", "Sensibilidad", "Búsqueda"],
    13: ["Empatía", "Intuición", "Cuidado", "Madurez"],
    14: ["Equilibrio emocional", "Sabiduría", "Diplomacia", "Cuidado"],
  },
  swords: {
    1:  ["Claridad", "Verdad", "Avance", "Decisión"],
    2:  ["Elección", "Punto muerto", "Equilibrio", "Pausa"],
    3:  ["Dolor", "Ruptura", "Pena", "Honestidad"],
    4:  ["Descanso", "Recuperación", "Pausa", "Silencio"],
    5:  ["Conflicto", "Victoria a cualquier precio", "Pérdidas", "Confrontación"],
    6:  ["Tránsito", "Movimiento", "Recuperación", "Esperanza"],
    7:  ["Astucia", "Estrategia", "Rodeo", "Cautela"],
    8:  ["Trampa", "Limitación", "Miedo", "Ilusión"],
    9:  ["Ansiedad", "Miedos", "Catastrofismo", "Noche"],
    10: ["Final", "Fondo", "Fin de ciclo", "Ascenso"],
    11: ["Mente aguda", "Curiosidad", "Franqueza", "Análisis"],
    12: ["Determinación", "Ambición", "Rapidez", "Conflicto"],
    13: ["Independencia", "Honestidad", "Mente aguda", "Experiencia"],
    14: ["Intelecto", "Justicia", "Autoridad", "Ética"],
  },
  pentacles: {
    1:  ["Arranque material", "Dinero", "Oportunidad", "Crecimiento"],
    2:  ["Equilibrio", "Adaptabilidad", "Recursos", "Flexibilidad"],
    3:  ["Maestría", "Trabajo en equipo", "Calidad", "Reconocimiento"],
    4:  ["Preservación", "Seguridad", "Finanzas", "Control"],
    5:  ["Necesidad", "Dificultades", "Ayuda", "Prueba"],
    6:  ["Generosidad", "Equilibrio", "Dar", "Recibir"],
    7:  ["Paciencia", "Evaluación", "Crecimiento", "Largo plazo"],
    8:  ["Maestría", "Trabajo", "Aprendizaje", "Aplicación"],
    9:  ["Independencia", "Abundancia", "Autosuficiencia", "Éxito"],
    10: ["Bienestar", "Legado", "Familia", "Cimiento"],
    11: ["Aplicación", "Aprendizaje", "Práctica", "Comienzo"],
    12: ["Método", "Fiabilidad", "Progreso", "Constancia"],
    13: ["Practicidad", "Cuidado", "Sabiduría financiera", "Hogar"],
    14: ["Abundancia", "Liderazgo", "Fiabilidad", "Sabiduría"],
  },
};

// Per-card advice for Minor Arcana (concise, actionable)
const MINOR_ADVICE: Record<string, Record<number, string>> = {
  wands: {
    1:  "Actúa mientras arde el fuego de la inspiración: deja las dudas para después.",
    2:  "Piensa más en grande. ¿Qué expansión estás aplazando?",
    3:  "Los primeros frutos ya se ven: sigue avanzando en la misma dirección.",
    4:  "Celebra lo logrado. La estabilidad es una buena base para el siguiente paso.",
    5:  "No evites el choque de ideas: en el debate nace la mejor solución.",
    6:  "Acepta el reconocimiento con dignidad. Tu éxito es merecido.",
    7:  "Defiende tu posición, pero elige tus batallas con sabiduría.",
    8:  "Actúa rápido: este momento no espera demoras.",
    9:  "Has llegado más lejos de lo que crees. Un último empujón y la meta está alcanzada.",
    10: "Delega. Cargar con todo en soledad no es valentía, sino un peso de más.",
    11: "Permítete ser principiante: justo eso abre posibilidades frescas.",
    12: "Actúa con audacia, pero con cuidado: la energía sin rumbo es destructiva.",
    13: "Tu seguridad es contagiosa. Úsala para inspirar a los demás.",
    14: "Piensa como líder. ¿Qué visión estás lista para hacer realidad ahora mismo?",
  },
  cups: {
    1:  "Abre el corazón. El nuevo sentimiento llega justo cuando estás lista para acogerlo.",
    2:  "El encuentro de dos es más que solo dos. Invierte en la unión.",
    3:  "Permítete disfrutar junto a los demás: eso también es una práctica espiritual.",
    4:  "Mira lo que te ofrece la vida: puede que hayas pasado por alto un regalo.",
    5:  "La pena es honestidad. Tras vivirla, verás lo que ha quedado en pie.",
    6:  "El pasado es fuente de calor, no una cárcel. Toma solo lo mejor.",
    7:  "Elige una cosa y entrégate por completo. El tiempo de los deseos difusos ha pasado.",
    8:  "A veces marcharse es el acto más honesto. Avanza hacia el sentido.",
    9:  "Estás más cerca de cumplir tus deseos de lo que crees. Deja que suceda.",
    10: "La verdadera felicidad está aquí y ahora, con quienes te rodean.",
    11: "Confía en las primeras impresiones: tu intuición está ahora especialmente acertada.",
    12: "Persigue el sueño, pero asegúrate de que es tuyo y no de otro.",
    13: "Escucha a los demás como te gustaría que te escucharan a ti.",
    14: "La madurez de los sentimientos es fuerza. Deja que tu sabiduría guíe tus decisiones.",
  },
  swords: {
    1:  "Corta las ilusiones. La claridad duele, pero libera.",
    2:  "Una elección sin resolver cuesta más que una decisión incómoda. Ya toca.",
    3:  "Reconoce el dolor: es el primer paso hacia la sanación.",
    4:  "Recuperarse también es una acción. Date una pausa sin culpa.",
    5:  "La victoria a cualquier precio destruye al vencedor. ¿Vale esta batalla ese precio?",
    6:  "Te estás alejando de la tormenta: confía en ese movimiento.",
    7:  "La astucia es útil, pero asegúrate de no estar engañándote ante todo a ti misma.",
    8:  "Las cadenas están en tu cabeza. Cambia tu forma de pensar y los muros desaparecerán.",
    9:  "Los miedos de la noche siempre son más grandes que la realidad. Espera a que amanezca.",
    10: "Has tocado fondo. La única dirección ahora es hacia arriba.",
    11: "Haz preguntas con valentía, pero cuida cómo transmites la verdad.",
    12: "Tu mente es tu herramienta más afilada. Dirígela a la creación.",
    13: "Tu franqueza es un valor. Suaviza las palabras, pero no la verdad.",
    14: "Toma decisiones con la razón, pero no olvides el corazón.",
  },
  pentacles: {
    1:  "El primer paso hacia tu meta material es ahora. La oportunidad es real.",
    2:  "Puedes hacer malabares, pero asegúrate de que ninguna bola caiga al suelo.",
    3:  "El trabajo en equipo multiplica el resultado. ¿A quién podrías sumar?",
    4:  "Guarda, pero no aprietes. El dinero cerrado en el puño no crece.",
    5:  "La ayuda está cerca: basta con pedirla. Pedir no es debilidad.",
    6:  "La verdadera abundancia circula. Da y recibe.",
    7:  "Los frutos requieren tiempo. No abandones el campo un día antes de la cosecha.",
    8:  "La maestría son miles de horas. Hoy has sumado una más.",
    9:  "Esto lo construiste tú misma. Disfrútalo sin culpa.",
    10: "Piensa en el legado. ¿Qué estás construyendo para los años venideros?",
    11: "Aprende con curiosidad, no por miedo a equivocarte.",
    12: "Lento y seguro. Tu ritmo es tu fuerza.",
    13: "El cuidado práctico también es amor. Estás creando seguridad.",
    14: "Tu éxito debe servir no solo a ti. Piensa más a lo grande.",
  },
};

// Build full 56-card Minor Arcana array
let minorId = 22;
export const MINOR_ARCANA: TarotCard[] = [];

for (const suit of ["wands", "cups", "swords", "pentacles"] as const) {
  const { emoji } = SUIT_ELEMENT[suit];
  for (let n = 1; n <= 14; n++) {
    const card = SUIT_CARDS[suit][n];
    const nameEn = n === 1
      ? `Ace of ${SUIT_EN[suit]}`
      : `${RANK_EN[n]} of ${SUIT_EN[suit]}`;
    MINOR_ARCANA.push({
      id: minorId++,
      number: n,
      arcana: "minor",
      suit,
      name: card.name,
      nameEn,
      emoji,
      keywords: MINOR_KEYWORDS[suit][n] ?? [],
      upright: card.upright,
      reversed: card.reversed,
      advice: MINOR_ADVICE[suit][n] ?? card.upright.split(".")[0] + ".",
    });
  }
}

export const ALL_CARDS: TarotCard[] = [...MAJOR_ARCANA, ...MINOR_ARCANA];

// ─── Reading helpers ──────────────────────────────────────────────────────────

/** Card of the day — deterministic by date + personal day number (Major Arcana only).
 *
 * Semántica: a cada usuario, cada día, le corresponde SU propia carta (personalización
 * por el día personal de la numerología del Tarot). Es el comportamiento esotérico
 * correcto.
 *
 * El caso límite «la carta cambia al cambiar la fecha de nacimiento» se resuelve a nivel
 * arquitectónico: BIRTH_DATE_LIMIT en src/lib/limits.ts — el usuario puede cambiar la
 * fecha un número limitado de veces (ahora 1). Tras el límite, el campo se bloquea en la
 * UI y el servidor rechaza el cambio → la carta del día es estable. */
export function getCardOfDay(personalDay: number, date?: Date): TarotCard {
  const d = date ?? new Date();
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate() + personalDay;
  const idx = seed % MAJOR_ARCANA.length;
  return MAJOR_ARCANA[idx];
}

export interface SpreadCard {
  card: TarotCard;
  isReversed: boolean;
}

/**
 * Three-card spread: Past / Present / Future
 * - All 3 cards unique and different from Card of Day
 * - ~50% chance of reversed position (deterministic per date)
 */
export function getThreeCardSpread(
  personalDay: number,
  date?: Date,
): [SpreadCard, SpreadCard, SpreadCard] {
  const d = date ?? new Date();
  const base = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate() + personalDay;
  const cardOfDayIdx = base % MAJOR_ARCANA.length;

  const used = new Set<number>([cardOfDayIdx]);
  const picks: SpreadCard[] = [];
  let attempt = 1;
  while (picks.length < 3) {
    const idx = (base + attempt * 13) % MAJOR_ARCANA.length;
    if (!used.has(idx)) {
      used.add(idx);
      // Mix card id into reversal hash — gives good distribution regardless of date parity
      const card = MAJOR_ARCANA[idx];
      const isReversed = (base + card.id * 7 + picks.length * 4) % 2 === 1;
      picks.push({ card, isReversed });
    }
    attempt++;
  }
  return picks as [SpreadCard, SpreadCard, SpreadCard];
}

export { SUIT_ELEMENT };

/**
 * Minor Arcana card of the day.
 * Suit is determined by personal month (fire/water/air/earth cycle),
 * specific card by personal day + date seed.
 */
export function getMinorCardOfDay(personalDay: number, personalMonth: number, date?: Date): TarotCard {
  const suitByMonth: Record<number, "wands" | "cups" | "swords" | "pentacles"> = {
    1: "wands",    2: "cups",      3: "swords",    4: "pentacles",
    5: "wands",    6: "cups",      7: "swords",    8: "pentacles",
    9: "wands",   10: "cups",     11: "swords",   12: "pentacles",
  };
  const suit = suitByMonth[personalMonth] ?? "wands";
  const suitCards = MINOR_ARCANA.filter((c) => c.suit === suit);
  const d = date ?? new Date();
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate() + personalDay + 37;
  return suitCards[seed % suitCards.length];
}

/**
 * Auto-generate a narrative connecting Past / Present / Future spread cards.
 */
export function buildSpreadNarrative(spread: [SpreadCard, SpreadCard, SpreadCard]): string {
  const [past, present, future] = spread;
  const key = (sc: SpreadCard) =>
    (sc.isReversed ? sc.card.keywords[1] ?? sc.card.keywords[0] : sc.card.keywords[0])?.toLowerCase() ?? "cambios";
  return (
    `«${past.card.name}» en el pasado señala la influencia del tema «${key(past)}»: sembró el terreno para ` +
    `lo que ocurre ahora. La carta del presente «${present.card.name}» habla de «${key(present)}» ` +
    `como la energía dominante de este momento. ` +
    `El vector del futuro «${future.card.name}» abre un camino a través de «${key(future)}»: justo hacia allí conduce la situación actual.`
  );
}
